import React, { type ReactNode, type MouseEvent } from 'react'
import styled from '@emotion/styled'
import { type SVGIcon } from 'ic-snacks'
import { NAV_ITEM_HEIGHT } from 'legacy/common/theme'
import RetailerRoute, { type RetailerRouteComponentProps } from 'legacy/components/RetailerRoute'
import { type RouteName } from 'utils/routing/routes'
import { SubNavItemContent } from 'legacy/components/Nav/SubNavItemContent'
import LinkBase from 'legacy/components/LinkBase'
import { type TestableProps } from 'legacy/common/utils/testing/testing.types'
import { withCoreContext } from 'utils/core/RPPCoreContext'
import { type RPPCoreContext } from 'utils/core/RPPCore.types'

const SubNavLink = styled(LinkBase)`
  display: block;
  text-decoration: none;
`

export const SubnavDescription = styled.div`
  text-decoration: none;
  overflow: hidden;
  height: ${NAV_ITEM_HEIGHT};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-left: 20px;
  &:visited {
    text-decoration: none;
  }
`

export interface SubNavItemProps extends TestableProps {
  route?: RouteName
  to?: string
  label?: string
  details?: string | ReactNode
  exact?: boolean
  externalLink?: string
  iconName?: React.ComponentProps<typeof SVGIcon>['name']
  q?: { [key: string]: string | string[] | number | number[] | boolean }
  elementAttributes?: {
    [key: string]: string | undefined | object
  }
  tabName?: string
  tabValue?: string
  defaultTab?: boolean
  onClick?: (e: MouseEvent<HTMLElement>) => void
  className?: string
  coreContext?: RPPCoreContext
}

class SubNavItem extends React.Component<SubNavItemProps> {
  isSelectedTab(query: { [key: string]: string }) {
    const { tabName, tabValue, defaultTab } = this.props
    const selectedTab = tabName && query[tabName]
    const selectByDefault = !selectedTab && defaultTab

    return Boolean(tabName && (selectByDefault || selectedTab === tabValue))
  }

  isActive(match: RetailerRouteComponentProps['match'], query: Record<string, string>) {
    const { externalLink, to, route } = this.props

    if (externalLink) {
      return false
    }

    if (to || route) {
      return Boolean(match)
    }

    return this.isSelectedTab(query)
  }

  getQuery() {
    const { q, tabName, tabValue } = this.props

    if (tabName) {
      return { ...q, [tabName]: tabValue }
    }

    return q
  }

  render() {
    const {
      label,
      details,
      exact,
      externalLink,
      iconName,
      to,
      route,
      children,
      elementAttributes,
      testId,
      onClick,
      className,
      coreContext,
    } = this.props
    const subNavLinkTestId = testId || `sub-nav-item-${label}`

    const { routesByName } = coreContext

    return (
      <SubNavLink
        elementAttributes={elementAttributes}
        data-testid={subNavLinkTestId}
        to={to}
        route={route}
        query={this.getQuery()}
        href={externalLink}
        onClick={onClick}
        className={className}
      >
        <RetailerRoute path={(route && routesByName[route]) || to} exact={exact}>
          {({ match, query }) => (
            <SubNavItemContent
              isActive={this.isActive(match, query as Record<string, string>)}
              label={label}
              details={details}
              iconName={iconName}
            >
              {children}
            </SubNavItemContent>
          )}
        </RetailerRoute>
      </SubNavLink>
    )
  }
}

export default withCoreContext(SubNavItem)
